import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { toOutboundMode, toSendPayload } from './schema';
import { uploadAttachments } from './upload-attachments';
import type { UploadedAttachment } from './upload-attachments';
import type { ComposerFormValues, EmailBody } from './schema';
import type { ComposerMode } from './types';
import type { Id } from '@minty/shared/ids';
import { appErrorCode } from '~/lib/app-error';
import { getPostHog } from '~/lib/posthog';
import { useTRPC } from '~/lib/trpc';

type SubmissionPhase = 'send' | 'upload';

type OutboundSubmissionOutcome = { ok: true } | { ok: false; message: string };

/**
 * The ordered outbound transaction: derive mode, reserve uploads, upload every
 * new file, then queue one Email. A request id survives every failed retry and
 * is released only after the server accepts the send.
 */
export function useOutboundSubmission() {
  const { t } = useTranslation('mailbox');
  const trpc = useTRPC();
  const { mutateAsync: generateOutboundUploadUrls } = useMutation(
    trpc.emails.outbound.send.generateOutboundUploadUrls.mutationOptions(),
  );
  const { mutateAsync: sendOutboundEmail } = useMutation(
    trpc.emails.outbound.send.sendOutboundEmail.mutationOptions(),
  );
  const clientRequestIdRef = React.useRef<string | null>(null);

  async function submit({
    body,
    mode,
    replyAll,
    sourceEmailId,
    values,
  }: {
    body: EmailBody;
    mode: ComposerMode;
    /** Which recipient set a reply was opened with. Not a form value: the
     *  composer states its mode rather than offering it, so nothing on screen
     *  can change this after it opens. */
    replyAll: boolean;
    sourceEmailId: Id<'emails'> | null;
    values: ComposerFormValues;
  }): Promise<OutboundSubmissionOutcome> {
    const outboundMode = toOutboundMode(values, mode, sourceEmailId, replyAll);
    const clientRequestId =
      clientRequestIdRef.current ?? globalThis.crypto.randomUUID();
    clientRequestIdRef.current = clientRequestId;
    let uploads: Array<UploadedAttachment>;

    try {
      const uploadUrls = await generateOutboundUploadUrls({
        files: values.attachments.map((file) => ({
          filename: file.name,
          size: file.size,
        })),
        keptForwardAttachmentIds: values.keptForwardAttachmentIds,
        mode: outboundMode,
      });

      uploads = await uploadAttachments(values.attachments, uploadUrls);
    } catch (error) {
      getPostHog()?.captureException(error);
      return failureFor(error, 'upload', mode);
    }

    try {
      await sendOutboundEmail(
        toSendPayload({
          body,
          clientRequestId,
          mode: outboundMode,
          uploads,
          values,
        }),
      );
    } catch (error) {
      getPostHog()?.captureException(error);
      return failureFor(error, 'send', mode);
    }

    clientRequestIdRef.current = null;
    return { ok: true };
  }

  function failureFor(
    error: unknown,
    phase: SubmissionPhase,
    mode: ComposerMode,
  ): Extract<OutboundSubmissionOutcome, { ok: false }> {
    const data = appErrorCode(error);

    if (data === 'forward_source_not_found') {
      return {
        ok: false,
        message: t('This message can no longer be forwarded.'),
      };
    }

    if (data === 'reply_source_not_found') {
      return {
        ok: false,
        message: t('This message can no longer be replied to.'),
      };
    }

    if (
      data === 'forward_attachment_not_found' ||
      data === 'forward_attachment_unavailable'
    ) {
      return {
        ok: false,
        message: t('An original attachment is unavailable.'),
      };
    }

    if (data === 'outbound_attachment_limit_exceeded') {
      return {
        ok: false,
        message: t('Attachments exceed the sending limits.'),
      };
    }

    if (data === 'outbound_content_limit_exceeded') {
      return {
        ok: false,
        message: t('The message exceeds the sending limit.'),
      };
    }

    return {
      ok: false,
      message:
        phase === 'upload'
          ? t('Unable to upload attachments.')
          : mode === 'forward'
            ? t('Unable to forward message.')
            : t('Unable to send message.'),
    };
  }

  return { submit };
}
